import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Chip,
    List,
    ListItemButton,
    ListItemText,
    Divider,
    Stack,
    TextField,
    Snackbar,
    Alert,
    Grid,
    Paper,
    Autocomplete
} from '@mui/material';
import axios from '../../../api/axios';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import { alpha } from '@mui/material/styles';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

// Dialog to list all addresses for a user (read-only)
import CreateAddressDialog from './CreateAddressDialog';

function AddressesManagementDialog({ open, onClose, userId, userName, userDefaults }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [settingDefault, setSettingDefault] = useState(false);
    const [areaList, setAreaList] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        const fetchAddresses = async () => {
            if (!open || !userId) return;
            setLoading(true);
            setError(null);
            try {
                const res = await axios.get('user/address/list/', { params: { user_id: userId } });
                let fetched = [];
                if (res.data && Array.isArray(res.data.addresses)) {
                    fetched = res.data.addresses;
                } else if (res.data && res.data.addresses && typeof res.data.addresses === 'object') {
                    const arr = Object.values(res.data.addresses);
                    fetched = Array.isArray(arr) ? arr : [];
                }
                setAddresses(fetched);
                // Select default if exists else first
                if (fetched.length > 0) {
                    const def = fetched.find(a => a.is_default);
                    setSelectedAddressId((def || fetched[0]).id);
                } else {
                    setSelectedAddressId(null);
                }
            } catch (e) {
                console.error('Failed to fetch addresses', e);
                setError('Failed to load addresses');
            } finally {
                setLoading(false);
            }
        };
        fetchAddresses();
    }, [open, userId]);

    // Fetch area list when dialog opens (once per open)
    useEffect(() => {
        const fetchAreas = async () => {
            if (!open) return;
            try {
                setLoadingAreas(true);
                const response = await axios.get('get_area_list/');
                if (Array.isArray(response.data)) {
                    setAreaList(response.data);
                } else if (response.data?.areas && Array.isArray(response.data.areas)) {
                    setAreaList(response.data.areas);
                } else if (response.data && typeof response.data === 'object') {
                    const areas = Object.keys(response.data).map(key => ({ name: key, ...response.data[key] }));
                    setAreaList(areas);
                } else {
                    setAreaList([]);
                }
            } catch (err) {
                console.error('Error loading area list', err);
            } finally {
                setLoadingAreas(false);
            }
        };
        fetchAreas();
    }, [open]);

    const selectedAddress = addresses.find(a => a.id === selectedAddressId) || null;

    // Sync form data when selection changes or when exiting edit mode
    useEffect(() => {
        if (selectedAddress && !isEditing) {
            setFormData({
                name: selectedAddress.name || '',
                receiver_full_name: selectedAddress.receiver_full_name || '',
                phone_number: selectedAddress.phone_number || '',
                area_name: selectedAddress.area_name || '',
                block: selectedAddress.block || '',
                street: selectedAddress.street || '',
                jaddah: selectedAddress.jaddah || '',
                house: selectedAddress.house || '',
                floor: selectedAddress.floor || '',
                apartment: selectedAddress.apartment || '',
                extra_direction: selectedAddress.extra_direction || '',
                inner_note: selectedAddress.inner_note || '',
                assigned_driver: selectedAddress.assigned_driver ?? ''
            });
        }
    }, [selectedAddress, isEditing]);

    const handleFieldChange = (field) => (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleEnterEdit = () => setIsEditing(true);
    const handleCancelEdit = () => {
        setIsEditing(false);
        // Reset to original values
        if (selectedAddress) {
            setFormData({
                name: selectedAddress.name || '',
                receiver_full_name: selectedAddress.receiver_full_name || '',
                phone_number: selectedAddress.phone_number || '',
                area_name: selectedAddress.area_name || '',
                block: selectedAddress.block || '',
                street: selectedAddress.street || '',
                jaddah: selectedAddress.jaddah || '',
                house: selectedAddress.house || '',
                floor: selectedAddress.floor || '',
                apartment: selectedAddress.apartment || '',
                extra_direction: selectedAddress.extra_direction || '',
                inner_note: selectedAddress.inner_note || '',
                assigned_driver: selectedAddress.assigned_driver ?? ''
            });
        }
    };

    const handleSave = async () => {
        if (!selectedAddress) return;
        // Validation: area is required
        if (!formData.area_name || formData.area_name.trim() === '') {
            setSnackbar({ open: true, message: 'Area is required', severity: 'error' });
            return;
        }
        const changed = {};
        Object.keys(formData).forEach(key => {
            if ((selectedAddress[key] || '') !== formData[key]) {
                changed[key] = formData[key];
            }
        });
        if (Object.keys(changed).length === 0) {
            setSnackbar({ open: true, message: 'No changes to save', severity: 'info' });
            setIsEditing(false);
            return;
        }
        setSaving(true);
        try {
            const payload = { address_id: selectedAddress.id, ...changed };
            await axios.put('user/address/update/', payload);
            // Update local state optimistically
            setAddresses(prev => prev.map(a => a.id === selectedAddress.id ? { ...a, ...changed } : a));
            setSnackbar({ open: true, message: 'Address updated', severity: 'success' });
            setIsEditing(false);
        } catch (err) {
            console.error('Failed to update address', err);
            setSnackbar({ open: true, message: 'Failed to update address', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleSetDefault = async () => {
        if (!selectedAddress || selectedAddress.is_default) return;
        setSettingDefault(true);
        try {
            await axios.post('user/address/set-default/', { address_id: selectedAddress.id });
            setAddresses(prev => prev.map(a => ({ ...a, is_default: a.id === selectedAddress.id })));
            setSnackbar({ open: true, message: 'Delivery address updated', severity: 'success' });
        } catch (err) {
            console.error('Failed to set default address', err);
            setSnackbar({ open: true, message: 'Failed to set delivery address', severity: 'error' });
        } finally {
            setSettingDefault(false);
        }
    };

    const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Typography variant="h6">Addresses - {userName}</Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Box sx={{ p: 2 }}>
                            <Typography color="error" variant="body2">{error}</Typography>
                        </Box>
                    ) : addresses.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No addresses found.</Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddLocationAltIcon />}
                                onClick={() => setCreateOpen(true)}
                                size="small"
                            >
                                Add Address
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', minHeight: 420 }}>
                            {/* Left List */}
                            <Box sx={{ width: 290, borderRight: theme => `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column', backgroundColor: theme => theme.palette.background.default }}>
                                <Box sx={{ p: 1, borderBottom: theme => `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'center' }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddLocationAltIcon fontSize="inherit" />}
                                        onClick={() => setCreateOpen(true)}
                                    >
                                        New
                                    </Button>
                                </Box>
                                <Paper variant="outlined" square sx={{ flex: 1, border: 'none', borderRadius: 0, overflowY: 'auto' }}>
                                    <List disablePadding sx={{ pb: 6 }}>
                                        {addresses.map(addr => {
                                            const secondary = `${addr.area_name || '-'} · ${addr.block || '-'} · ${addr.street || '-'}`;
                                            return (
                                                <React.Fragment key={addr.id}>
                                                    <ListItemButton
                                                        selected={addr.id === selectedAddressId}
                                                        onClick={() => setSelectedAddressId(addr.id)}
                                                        alignItems="flex-start"
                                                        sx={(theme) => ({
                                                            py: 1,
                                                            px: 1.5,
                                                            gap: 0.5,
                                                            borderLeft: '3px solid transparent',
                                                            '&.Mui-selected': {
                                                                backgroundColor: alpha(theme.palette.primary.main, 0.16),
                                                                borderLeftColor: theme.palette.primary.main,
                                                                '& .MuiTypography-subtitle2': { color: theme.palette.primary.main },
                                                            },
                                                            '&.Mui-selected:hover': {
                                                                backgroundColor: alpha(theme.palette.primary.main, 0.24),
                                                            }
                                                        })}
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Typography variant="subtitle2" noWrap sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                                        {addr.name || 'Unnamed'}
                                                                    </Typography>
                                                                    {addr.is_default && (
                                                                        <Chip
                                                                            icon={<LocalShippingIcon sx={{ fontSize: '0.9rem !important' }} />}
                                                                            label="Delivery"
                                                                            size="small"
                                                                            color="success"
                                                                            sx={{
                                                                                height: 20,
                                                                                pl: 0.6,
                                                                                '& .MuiChip-icon': { ml: 0, mr: 0.4 },
                                                                                fontSize: '0.65rem'
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            }
                                                            secondary={
                                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem' }}>
                                                                    {secondary}
                                                                </Typography>
                                                            }
                                                            sx={{ my: 0 }}
                                                        />
                                                    </ListItemButton>
                                                    <Divider component="li" />
                                                </React.Fragment>
                                            );
                                        })}
                                    </List>
                                </Paper>
                            </Box>

                            {/* Right Details */}
                            <Box sx={{ flex: 1, p: 2.5, overflowY: 'auto', position: 'relative' }}>
                                {selectedAddress ? (
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Stack spacing={1.25}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {isEditing ? (
                                                        <TextField
                                                            label="Address Name"
                                                            size="small"
                                                            value={formData.name || ''}
                                                            onChange={handleFieldChange('name')}
                                                            sx={{ width: 220 }}
                                                        />
                                                    ) : (
                                                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '1rem', mb: 0 }}>
                                                            {selectedAddress.name || 'Address'}
                                                            {selectedAddress.is_default && (
                                                                <Chip
                                                                    icon={<LocalShippingIcon sx={{ fontSize: '1rem !important' }} />}
                                                                    label="Delivery"
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{
                                                                        pl: 0.7,
                                                                        '& .MuiChip-icon': { ml: 0, mr: 0.5 },
                                                                        fontSize: '0.7rem'
                                                                    }}
                                                                />
                                                            )}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="caption" sx={{ opacity: 0.7 }}>ID: {selectedAddress.id}</Typography>
                                                </Box>
                                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                                    {!selectedAddress.is_default && !isEditing && (
                                                        <Button size="small" variant="outlined" startIcon={<LocalShippingIcon fontSize="inherit" />} onClick={handleSetDefault} disabled={settingDefault}>
                                                            {settingDefault ? 'Setting…' : 'Set Delivery'}
                                                        </Button>
                                                    )}
                                                    {!isEditing && (
                                                        <Button size="small" variant="outlined" onClick={handleEnterEdit}>Edit</Button>
                                                    )}
                                                    {isEditing && (
                                                        <>
                                                            <Button size="small" variant="outlined" onClick={handleCancelEdit} disabled={saving}>Cancel</Button>
                                                            <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
                                                        </>
                                                    )}
                                                </Stack>
                                            </Box>
                                            <Divider />
                                            {/* Compact grid of fields */}
                                            <Grid container spacing={1}>
                                                {['receiver_full_name', 'phone_number', 'area_name', 'block', 'street', 'jaddah', 'house', 'floor', 'apartment', 'assigned_driver'].map(field => {
                                                    const labelMap = {
                                                        receiver_full_name: 'Receiver',
                                                        phone_number: 'Phone',
                                                        area_name: 'Area',
                                                        block: 'Block',
                                                        street: 'Street',
                                                        jaddah: 'Jaddah',
                                                        house: 'House',
                                                        floor: 'Floor',
                                                        apartment: 'Apartment',
                                                        assigned_driver: 'Driver'
                                                    };
                                                    return (
                                                        <Grid key={field} item xs={12} sm={6} md={4} lg={3}>
                                                            {isEditing ? (
                                                                ['receiver_full_name', 'phone_number'].includes(field) ? (
                                                                    <TextField
                                                                        label={labelMap[field]}
                                                                        size="small"
                                                                        value={formData[field] || ''}
                                                                        fullWidth
                                                                        margin="dense"
                                                                        disabled
                                                                    />
                                                                ) : (
                                                                    field === 'area_name' ? (
                                                                        <Autocomplete
                                                                            size="small"
                                                                            loading={loadingAreas}
                                                                            options={areaList}
                                                                            disablePortal
                                                                            getOptionLabel={(option) => {
                                                                                if (typeof option === 'string') return option;
                                                                                return option.name || option.area || '';
                                                                            }}
                                                                            isOptionEqualToValue={(option, value) => {
                                                                                if (typeof option === 'string' && typeof value === 'string') return option === value;
                                                                                const optName = typeof option === 'string' ? option : (option.name || option.area || '');
                                                                                const valName = typeof value === 'string' ? value : (value.name || value.area || '');
                                                                                return optName === valName;
                                                                            }}
                                                                            value={formData.area_name || null}
                                                                            onChange={(e, newValue) => {
                                                                                let areaValue = '';
                                                                                if (newValue) {
                                                                                    if (typeof newValue === 'string') areaValue = newValue;
                                                                                    else areaValue = newValue.name || newValue.area || '';
                                                                                }
                                                                                setFormData(prev => ({ ...prev, area_name: areaValue }));
                                                                            }}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    label={labelMap[field]}
                                                                                    required
                                                                                    error={!formData.area_name || formData.area_name.trim() === ''}
                                                                                    helperText={!formData.area_name || formData.area_name.trim() === '' ? 'Required' : ''}
                                                                                    margin="dense"
                                                                                    placeholder="Select area"
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        endAdornment: (
                                                                                            <>
                                                                                                {loadingAreas ? <CircularProgress color="inherit" size={16} /> : null}
                                                                                                {params.InputProps.endAdornment}
                                                                                            </>
                                                                                        )
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        />
                                                                    ) : (
                                                                        <TextField
                                                                            label={labelMap[field]}
                                                                            size="small"
                                                                            value={formData[field] || ''}
                                                                            onChange={handleFieldChange(field)}
                                                                            fullWidth
                                                                            margin="dense"
                                                                        />
                                                                    )
                                                                )
                                                            ) : (
                                                                <KeyValueCompact label={labelMap[field]} value={selectedAddress[field]} />
                                                            )}
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                            {/* Notes */}
                                            <Grid container spacing={1}>
                                                <Grid item xs={12} md={6}>
                                                    {isEditing ? (
                                                        <TextField
                                                            label="Extra Direction"
                                                            size="small"
                                                            multiline
                                                            minRows={2}
                                                            maxRows={5}
                                                            value={formData.extra_direction || ''}
                                                            onChange={handleFieldChange('extra_direction')}
                                                            fullWidth
                                                            margin="dense"
                                                        />
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.7 }}>Extra Direction (By User)</Typography>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', p: 1, borderRadius: 1, backgroundColor: theme => theme.palette.action.hover, fontSize: '0.75rem', minHeight: 64 }}>
                                                                {selectedAddress.extra_direction || '—'}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    {isEditing ? (
                                                        <TextField
                                                            label="Inner Note"
                                                            size="small"
                                                            multiline
                                                            minRows={2}
                                                            maxRows={5}
                                                            value={formData.inner_note || ''}
                                                            onChange={handleFieldChange('inner_note')}
                                                            fullWidth
                                                            margin="dense"
                                                        />
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.7 }}>Inner Note</Typography>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', p: 1, borderRadius: 1, backgroundColor: theme => theme.palette.action.hover, fontSize: '0.75rem', minHeight: 64 }}>
                                                                {selectedAddress.inner_note || '—'}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Stack>
                                    </Paper>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">Select an address to view details.</Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} variant="contained" color="primary" size="small">Close</Button>
                </DialogActions>
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Dialog>
            <CreateAddressDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                userId={userId}
                userDefaults={userDefaults}
                onCreated={(newAddr) => {
                    setAddresses(prev => {
                        let updated = prev;
                        // If new address is default, clear previous default flags
                        if (newAddr.is_default) {
                            updated = prev.map(a => ({ ...a, is_default: false }));
                        }
                        updated = [...updated, newAddr];
                        return updated;
                    });
                    setSelectedAddressId(newAddr.id);
                    setCreateOpen(false);
                }}
            />
        </>
    );
}

// Small helper components for consistent key/value presentation
function KeyValueCompact({ label, value }) {
    return (
        <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block', textTransform: 'capitalize', opacity: 0.7 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                {value || '-'}
            </Typography>
        </Box>
    );
}

export default AddressesManagementDialog;
